import type { Definition } from '../api/missions';

type Person = NonNullable<Definition['cast']>[number] & { locked?: boolean };

/** Every string of a value with `from` said `to`; ids and keys stay as they are. */
function renamed(value: unknown, from: string, to: string, key?: string): unknown {
  if (typeof value === 'string') return key === 'id' ? value : value.split(from).join(to);
  if (Array.isArray(value)) return value.map((v) => renamed(v, from, to));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, renamed(v, from, to, k)]));
  }
  return value;
}

/**
 * A person's name changed in the cast and wherever the mission says it (the summary, the briefing,
 * a scene's lines): the same string said again, so a name never stays behind in a sentence.
 */
export function renameCast(def: Definition, id: string, name: string): Definition {
  const person = def.cast?.find((c) => c.id === id);
  const from = person?.name ?? '';
  if (!person || !name.trim() || name === from) return def;
  const next = from ? (renamed(def, from, name) as Definition) : def;
  return {
    ...next,
    cast: (next.cast ?? []).map((c) => (c.id === id ? { ...c, name } : c)),
  };
}

/** One person's fields changed. */
export function editCast(def: Definition, id: string, patch: Partial<Person>): Definition {
  return { ...def, cast: (def.cast ?? []).map((c) => (c.id === id ? { ...c, ...patch } : c)) };
}

/** The id the next added person takes: `added-1`, `added-2`… */
export function nextCastId(def: Definition): string {
  const taken = new Set((def.cast ?? []).map((c) => c.id));
  for (let n = 1; ; n++) if (!taken.has(`added-${n}`)) return `added-${n}`;
}

export function addCast(def: Definition, name: string): Definition {
  const person: Person = { id: nextCastId(def), name, role: '', disposition: 'neutral' };
  return { ...def, cast: [...(def.cast ?? []), person] };
}

export function removeCast(def: Definition, id: string): Definition {
  return { ...def, cast: (def.cast ?? []).filter((c) => c.id !== id) };
}

/** Only a person an editor added can be removed: the others are the script's or the draw's. */
export const canRemove = (id: string) => id.startsWith('added-');
